import React, { useEffect, useState } from "react";
import { ArrowRight, ClipboardCheck, Lightbulb, Calculator, GraduationCap, Quote, Download } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { COMPANY } from "../content";
import { useLanguage } from "../i18n";
import { SectionTitle, PillButton, Reveal } from "../components/Shared";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_ICONS = {
  audit: ClipboardCheck,
  advisory: Lightbulb,
  "expertise-comptable": Calculator,
  formations: GraduationCap,
};

const Home = () => {
  const [slide, setSlide] = useState(0);
  const [articles, setArticles] = useState([]);
  const { lang, t, data } = useLanguage();
  const dateLocale = lang === "fr" ? fr : enUS;

  useEffect(() => {
    const timer = setInterval(() => setSlide((s) => (s + 1) % data.heroSlides.length), 6000);
    return () => clearInterval(timer);
  }, [data.heroSlides.length]);

  useEffect(() => {
    axios
      .get(`${API}/articles`)
      .then((res) => setArticles(res.data.slice(0, 3)))
      .catch(() => setArticles([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* ============ HERO SLIDER ============ */}
      <section className="relative h-screen min-h-[620px] overflow-hidden" data-testid="hero-section">
        {data.heroSlides.map((s, i) => (
          <div
            key={s.id}
            className={`absolute inset-0 transition-opacity duration-[1500ms] ease-in-out ${
              i === slide ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={s.image}
              alt="INEXPONENS"
              className={`w-full h-full object-cover hero-zoom ${i === slide ? "hero-zoom-active" : ""}`}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#141d43]/85 via-[#141d43]/60 to-[#141d43]/35" />
          </div>
        ))}

        <div className="relative z-10 h-full max-w-7xl mx-auto px-5 lg:px-8 flex flex-col justify-center">
          <div key={`${slide}-${lang}`} className="hero-text">
            <h1 className="text-white text-4xl md:text-6xl font-semibold leading-[1.15] tracking-wide max-w-3xl">
              {data.heroSlides[slide].line1},
              <br />
              {data.heroSlides[slide].line2}
            </h1>
            <div className="mt-10">
              <PillButton to="/contact">{t.contactUs}</PillButton>
            </div>
          </div>
        </div>

        {/* Dots */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex gap-3">
          {data.heroSlides.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setSlide(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-2.5 rounded-full transition-all duration-500 ${
                i === slide ? "w-8 bg-white" : "w-2.5 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      </section>

      {/* ============ LE CABINET ============ */}
      <section className="py-24 bg-white" data-testid="cabinet-section">
        <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <Reveal>
            <SectionTitle>{t.home.cabinetTitle}</SectionTitle>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-slate-600 leading-loose text-[15.5px] md:text-base font-light">
              <strong className="font-semibold text-[#1b2653]">{COMPANY.fullName}</strong>
              {t.home.cabinetP1a}
            </p>
            <p className="text-slate-600 leading-loose text-[15.5px] md:text-base font-light mt-6">
              {t.home.cabinetP2}
            </p>
          </Reveal>
          <Reveal delay={200}>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <PillButton to="/rendez-vous" dark>{t.requestRdv}</PillButton>
              <a
                href="/plaquette-inexponens.pdf"
                download="Plaquette-INEXPONENS.pdf"
                className="inline-flex items-center gap-2.5 rounded-full px-8 py-3.5 text-[13px] font-semibold uppercase tracking-[0.18em] border-2 border-[#1b2653] text-[#1b2653] hover:bg-[#1b2653] hover:text-white transition-colors duration-300"
                data-testid="brochure-download-btn"
              >
                <Download size={16} />
                {t.brochure}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ STATS ============ */}
      <section className="bg-[#1b2653] py-14" data-testid="stats-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-10">
          {data.stats.map((s, i) => (
            <Reveal key={s.id} delay={i * 100} className="text-center">
              <div className="text-4xl md:text-5xl font-semibold text-white">{s.value}</div>
              <div className="text-[#8fb5f5] text-xs md:text-sm uppercase tracking-[0.18em] mt-3">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ QUI SOMMES-NOUS (3 valeurs) ============ */}
      <section className="py-24 bg-[#f5f7fb]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <SectionTitle>{t.home.valuesTitle}</SectionTitle>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-4">
            {data.values.map((v, i) => (
              <Reveal key={v.id} delay={i * 120}>
                <div className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-500 h-full">
                  <div className="relative overflow-hidden">
                    <img
                      src={v.image}
                      alt={v.title}
                      className="w-full h-52 object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute bottom-0 left-6 right-6 translate-y-1/2">
                      <div className="bg-[#2547a8] text-white text-center py-3 rounded-md text-lg font-medium tracking-wide shadow-lg">
                        {v.title}
                      </div>
                    </div>
                  </div>
                  <p className="px-7 pt-12 pb-8 text-slate-600 text-[14.5px] leading-relaxed font-light">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ NOS SERVICES ============ */}
      <section className="py-24 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <SectionTitle>{t.home.servicesTitle}</SectionTitle>
            <p className="text-center text-slate-500 font-light -mt-6 mb-14">{t.home.servicesSubtitle}</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {data.services.map((s, i) => {
              const Icon = SERVICE_ICONS[s.id];
              return (
                <Reveal key={s.id} delay={i * 100}>
                  <Link
                    to="/services"
                    onClick={() => window.scrollTo({ top: 0 })}
                    className="group flex gap-6 p-8 rounded-lg border border-slate-200 hover:border-[#2f6fd8] hover:shadow-lg transition-all duration-400 h-full"
                    data-testid={`service-card-${s.id}`}
                  >
                    <div className="shrink-0 w-14 h-14 rounded-full bg-[#eef3fc] flex items-center justify-center group-hover:bg-[#1b2653] transition-colors duration-400">
                      <Icon size={26} className="text-[#2547a8] group-hover:text-white transition-colors duration-400" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-[#1b2653] mb-2.5 flex items-center gap-2">
                        {s.title}
                        <ArrowRight size={17} className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 text-[#2f6fd8]" />
                      </h3>
                      <p className="text-slate-600 text-[14.5px] leading-relaxed font-light">{s.short}</p>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={150}>
            <div className="text-center mt-12">
              <PillButton to="/services" dark>{t.home.discoverSolutions}</PillButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ FONDATEUR ============ */}
      <section className="py-24 bg-[#f5f7fb]" data-testid="founder-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <div className="relative max-w-md mx-auto lg:mx-0">
                <div className="absolute -top-4 -left-4 w-full h-full rounded-lg bg-[#2547a8]/10" />
                <img
                  src={data.founder.photo}
                  alt={data.founder.name}
                  className="relative rounded-lg shadow-xl w-full object-cover"
                />
              </div>
            </Reveal>
            <Reveal delay={120}>
              <span className="text-[#2f6fd8] text-xs font-semibold uppercase tracking-[0.25em]">{t.home.founderLabel}</span>
              <h2 className="text-3xl md:text-4xl font-semibold text-[#1b2653] mt-3">{data.founder.name}</h2>
              <p className="text-[#2547a8] font-medium mt-2">{data.founder.role}</p>
              <p className="text-slate-600 leading-loose font-light mt-6 text-[15px]">{data.founder.intro}</p>
              <p className="text-slate-600 leading-loose font-light mt-4 text-[15px]">{data.founder.bio[3]}</p>
              <div className="mt-8">
                <PillButton to="/equipe" dark>{t.home.discoverTeam}</PillButton>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ TÉMOIGNAGES ============ */}
      <section className="py-24 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal>
            <SectionTitle>{t.home.testimonialsTitle}</SectionTitle>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {data.testimonials.map((tm, i) => (
              <Reveal key={tm.id} delay={i * 120}>
                <div className="bg-[#f5f7fb] rounded-lg p-8 h-full flex flex-col hover:shadow-lg transition-shadow duration-500">
                  <Quote size={30} className="text-[#2f6fd8]/40 mb-4" />
                  <p className="text-slate-600 text-[14.5px] leading-relaxed font-light flex-1 italic">« {tm.text} »</p>
                  <div className="mt-6 pt-5 border-t border-slate-200">
                    <div className="font-semibold text-[#1b2653] text-sm">{tm.name}</div>
                    <div className="text-slate-500 text-xs mt-1">{tm.company}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ACTUALITÉS ============ */}
      {articles.length > 0 && (
        <section className="py-24 bg-[#f5f7fb]" data-testid="home-news-section">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <Reveal>
              <SectionTitle>{t.home.newsTitle}</SectionTitle>
              <p className="text-center text-slate-500 font-light -mt-6 mb-14">{t.home.newsSubtitle}</p>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {articles.map((a, i) => (
                <Reveal key={a.id} delay={i * 110}>
                  <Link
                    to={`/actualites/${a.id}`}
                    onClick={() => window.scrollTo({ top: 0 })}
                    className="group bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-shadow duration-500 flex flex-col h-full"
                    data-testid={`home-article-${a.id}`}
                  >
                    {a.image && (
                      <div className="overflow-hidden h-48">
                        <img
                          src={a.image}
                          alt={a.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      </div>
                    )}
                    <div className="p-7 flex flex-col flex-1">
                      {a.category && (
                        <span className="text-[#2f6fd8] text-[11px] font-semibold uppercase tracking-[0.2em] mb-3">
                          {a.category}
                        </span>
                      )}
                      <h3 className="font-semibold text-[#1b2653] leading-snug mb-3 group-hover:text-[#2547a8] transition-colors duration-300">
                        {a.title}
                      </h3>
                      <p className="text-slate-500 text-sm font-light leading-relaxed flex-1">
                        {a.content.slice(0, 130)}...
                      </p>
                      <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100">
                        <span className="text-slate-400 text-xs">
                          {format(new Date(a.created_at), "d MMMM yyyy", { locale: dateLocale })}
                        </span>
                        <span className="text-[#2547a8] text-sm font-medium inline-flex items-center gap-1.5">
                          {t.home.readMore}
                          <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
            <Reveal delay={150}>
              <div className="text-center mt-12">
                <PillButton to="/actualites" dark>{t.home.allNews}</PillButton>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* ============ CTA FINAL ============ */}
      <section className="relative py-24 overflow-hidden" data-testid="cta-section">
        <img
          src="https://images.unsplash.com/photo-1462396240927-52058a6a84ec?w=1920&q=80"
          alt="INEXPONENS"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#141d43]/85" />
        <div className="relative z-10 max-w-4xl mx-auto px-5 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-4xl font-semibold text-white leading-snug">{t.home.ctaTitle}</h2>
            <p className="text-white/75 font-light mt-5 max-w-2xl mx-auto">{t.home.ctaText}</p>
            <div className="mt-10">
              <PillButton to="/contact">{t.contactUs}</PillButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
